import { useTranslation, Trans } from 'react-i18next';
import { MarketButton, MarketLink } from '@market/react';
import { withI18n } from '@squareup/service-i18n/components';
import { InstantAccessProgram_Platform } from '@squareup/shared-types-protos/squareup/inflows/instant_access_program_model';
// Hero WebP images
import type { DDSEligibleUnitInfo_IntegrationToOptInStatus as IntegrationToOptInStatus } from '@squareup/shared-types-protos/squareup/inflows/service/external_instant_access_service.js';
import setupPageHeroSmallWebp from '../../assets/images/setup-page-hero-small.webp';
import setupPageHeroLargeWebp from '../../assets/images/setup-page-hero-large.webp';
// Hero PNG images with retina support
import setupPageHeroSmallPng from '../../assets/images/setup-page-hero-small.png';
import setupPageHeroSmall2xPng from '../../assets/images/setup-page-hero-small@2x.png';
import setupPageHeroSmall3xPng from '../../assets/images/setup-page-hero-small@3x.png';
import setupPageHeroLargePng from '../../assets/images/setup-page-hero-large.png';
import setupPageHeroLarge2xPng from '../../assets/images/setup-page-hero-large@2x.png';
import setupPageHeroLarge3xPng from '../../assets/images/setup-page-hero-large@3x.png';
import useResponsiveBreakpoint from '../../hooks/useResponsiveBreakpoint.js';
import DoorDashSvg from '../../assets/svgs/doordash';
import UberEatsSvg from '../../assets/svgs/uber-eats';
import styles from './SetupPage.module.css';

interface SetupPageProps {
  availableIntegrations?: IntegrationToOptInStatus[]; // information about the integrated platforms that may or may not have opted in for instant payouts
  onSetupClick?: (selectedIntegration?: IntegrationToOptInStatus) => void;
  isLoading: boolean;
  locationName: string;
}

export const SetupPage = ({
  availableIntegrations = [], onSetupClick, isLoading, locationName,
}: SetupPageProps) => {
  const { t } = useTranslation();
  const isMobile = useResponsiveBreakpoint();
  const TOS_LINK = 'https://squareup.com/campaign/instant-payouts-terms';
  const LEARN_MORE_LINK = 'https://squareup.com/help/us/en/article/8319-instant-payouts-with-delivery-partners';
  const DOORDASH_LABEL = 'DoorDash';
  const UBER_EATS_LABEL = 'Uber Eats';

  const hasSingleIntegration = availableIntegrations.length === 1;
  const hasMultipleIntegrations = availableIntegrations.length > 1;
  const shouldShowLearnMoreLink = hasMultipleIntegrations; // show Learn more as a link in description if there are multiple integrated platforms
  const shouldShowLearnMoreButton = !hasMultipleIntegrations; // show Learn more as a button if there is one or none integrated platform

  const getPlatformLabel = (platform?: InstantAccessProgram_Platform) => {
    switch (platform) {
      case InstantAccessProgram_Platform.DOORDASH:
        return DOORDASH_LABEL;
      case InstantAccessProgram_Platform.UBER_EATS:
        return UBER_EATS_LABEL;
      default:
        return '';
    }
  };

  const getPlatformLogo = (platform?: InstantAccessProgram_Platform) => {
    switch (platform) {
      case InstantAccessProgram_Platform.DOORDASH:
        return <span slot="icon"><DoorDashSvg /></span>;
      case InstantAccessProgram_Platform.UBER_EATS:
        return <span slot="icon"><UberEatsSvg /></span>;
      default:
        return null;
    }
  };

  const renderDescription = () => (
    <>
      {hasSingleIntegration
        ? (
          <Trans
            i18nKey="bankingInstantPayouts.setupPage.descriptionForSinglePlatform"
            values={{
              platformLabel: getPlatformLabel(availableIntegrations[0]?.platform),
            }}
          />
        )
        : (
          <Trans i18nKey="bankingInstantPayouts.setupPage.description" />
        )}

      {shouldShowLearnMoreLink && (
        <span>
        &nbsp;
          <MarketLink
            className={styles.link}
            href={LEARN_MORE_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('bankingInstantPayouts.setupPage.learnMore')}
          </MarketLink>
        </span>
      )}
    </>
  );

  const renderSetupButtons = () => {
    if (hasSingleIntegration) {
      const buttonLabel = isMobile ?
        t('bankingInstantPayouts.setupPage.switchPayoutButtonMobile')
        : t('bankingInstantPayouts.setupPage.switchPayoutButton');
      return (
        <MarketButton
          rank="primary"
          isLoading={isLoading}
          onClick={() => onSetupClick?.(availableIntegrations[0])}
        >
          {buttonLabel}
        </MarketButton>
      );
    }
    if (hasMultipleIntegrations) {
      return availableIntegrations.map((availableIntegration) => {
        const platformLabel = getPlatformLabel(availableIntegration?.platform);
        return (
          <MarketButton
            key={platformLabel}
            rank="secondary"
            isLoading={isLoading}
            onClick={() => onSetupClick?.(availableIntegration)}
          >
            {getPlatformLogo(availableIntegration?.platform)}
            {t('bankingInstantPayouts.setupPage.switchPayoutButtonWithPlatformLabel', { platformLabel })}
          </MarketButton>
        );
      });
    }
    return (
      <MarketButton
        rank="primary"
        isLoading={isLoading}
        onClick={() => onSetupClick?.()}
      >
        {t('bankingInstantPayouts.setupPage.setupButton')}
      </MarketButton>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 data-testid="setup-page-title" className={`heading-30 ${styles.title}`}>
          {t('bankingInstantPayouts.setupPage.title', { locationName })}
        </h1>

        <p data-testid="setup-page-description" className={`${styles.description} paragraph-30`}>
          {renderDescription()}
        </p>

        <div className={styles.requirements}>
          <p className={`paragraph-10 ${styles.requirements}`}>
            <Trans
              i18nKey="bankingInstantPayouts.setupPage.requirementsAndTerms"
              components={{
                termsLink: (
                  <MarketLink
                    className={styles.link}
                    href={TOS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <div className={styles.actions}>
        {renderSetupButtons()}
        {shouldShowLearnMoreButton &&
          <MarketButton
            rank="secondary"
            href={LEARN_MORE_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('bankingInstantPayouts.setupPage.learnMore')}
          </MarketButton>}
      </div>

      {/* WebP with mobile/desktop variants, falling back to PNG with 1x/2x/3x for retina displays */}
      <picture>
        <source
          media="(max-width: 800px)"
          srcSet={setupPageHeroSmallWebp}
          type="image/webp"
        />
        <source
          srcSet={setupPageHeroLargeWebp}
          type="image/webp"
        />
        <source
          media="(max-width: 800px)"
          srcSet={`${setupPageHeroSmallPng} 1x, ${setupPageHeroSmall2xPng} 2x, ${setupPageHeroSmall3xPng} 3x`}
          type="image/png"
        />
        <source
          srcSet={`${setupPageHeroLargePng} 1x, ${setupPageHeroLarge2xPng} 2x, ${setupPageHeroLarge3xPng} 3x`}
          type="image/png"
        />
        <img
          src={setupPageHeroLargePng}
          alt=""
          className={styles.hero}
        />
      </picture>
    </div>
  );
};

export const SetupPageI18n = withI18n(SetupPage);
